import { supabase } from '@/integrations/supabase/client';

/**
 * Add sample analytics data for better metrics visualization
 */
export const addSampleAnalyticsData = async () => {
  try {
    // Create multiple leads over the past week with different dates
    const sampleLeads = [];
    const sampleContacts = [];

    for (let i = 7; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      // Create 2-5 leads per day
      const leadsPerDay = Math.floor(Math.random() * 4) + 2;

      for (let j = 0; j < leadsPerDay; j++) {
        const leadId = crypto.randomUUID();

        // Random product recommendations
        const allProducts = ['property_all_risks', 'public_liability', 'workmens_comp', 'employee_medical', 'cyber', 'fleet', 'professional_indemnity'];
        const numProducts = Math.floor(Math.random() * 3) + 2;
        const recommendedProducts = allProducts.slice(0, numProducts);

        sampleLeads.push({
          id: leadId,
          licence_location: ['mainland', 'free_zone', 'difc_adgm'][Math.floor(Math.random() * 3)],
          physical_property: ['own', 'lease', 'none'][Math.floor(Math.random() * 3)],
          client_visits: ['high', 'medium', 'low', 'none'][Math.floor(Math.random() * 4)],
          cyber_concerns: ['high', 'medium', 'low', 'none'][Math.floor(Math.random() * 4)],
          professional_advice: ['core', 'sometimes', 'minimal', 'none'][Math.floor(Math.random() * 4)],
          recommended_products: recommendedProducts,
          created_at: date.toISOString()
        });

        // Add contact for each lead
        const names = ['Ahmed Al-Rashid', 'Sarah Johnson', 'Mohammed Hassan', 'Emily Chen', 'Omar Abdullah', 'Lisa Thompson'];
        const companies = ['Tech Solutions LLC', 'Gulf Trading Co', 'Emirates Consulting', 'Digital Innovations', 'Construction Plus', 'Healthcare Partners'];

        sampleContacts.push({
          lead_id: leadId,
          name: names[Math.floor(Math.random() * names.length)],
          email: `contact${j}${i}@${companies[Math.floor(Math.random() * companies.length)].toLowerCase().replace(/\s+/g, '')}.com`,
          phone: `+971${Math.floor(Math.random() * 9) + 1}${Math.floor(Math.random() * 9000000) + 1000000}`
        });
      }
    }

    // Insert leads
    const { data: insertedLeads, error: leadsError } = await supabase
      .from('leads')
      .insert(sampleLeads)
      .select();

    if (leadsError) throw leadsError;

    // Insert contacts
    const { data: insertedContacts, error: contactsError } = await supabase
      .from('lead_contacts')
      .insert(sampleContacts)
      .select();

    if (contactsError) throw contactsError;

    return {
      success: true,
      message: `Added ${insertedLeads?.length || 0} leads and ${insertedContacts?.length || 0} contacts for analytics!`,
      data: { leads: insertedLeads, contacts: insertedContacts }
    };

  } catch (error) {
    console.error('Error adding sample analytics data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Add sample file uploads for testing downloads
 */
export const addSampleFileUploads = async () => {
  try {
    // Get existing submissions
    const { data: submissions, error: submissionsError } = await supabase
      .from('submissions')
      .select('id')
      .limit(3);

    if (submissionsError) throw submissionsError;

    if (!submissions || submissions.length === 0) {
      throw new Error('No submissions found. Please add sample submissions first.');
    }

    // Sample file upload records (simulating uploaded files)
    const sampleUploads = [
      {
        submission_id: submissions[0]?.id,
        file_path: 'trade-licenses/trade_license_001.pdf',
        doc_type: 'trade_license',
        mime_type: 'application/pdf',
        size_bytes: 245760 // ~240KB
      },
      {
        submission_id: submissions[1]?.id,
        file_path: 'trade-licenses/trade_license_002.pdf',
        doc_type: 'trade_license',
        mime_type: 'application/pdf',
        size_bytes: 189440 // ~185KB
      },
      {
        submission_id: submissions[2]?.id,
        file_path: 'fleet-files/fleet_data_001.xlsx',
        doc_type: 'fleet_file',
        mime_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        size_bytes: 15360 // ~15KB
      }
    ].filter(upload => upload.submission_id); // Only include uploads with valid submission IDs

    if (sampleUploads.length === 0) {
      throw new Error('No valid submissions found for file uploads');
    }

    // Insert sample upload records
    const { data: uploads, error: uploadsError } = await supabase
      .from('uploads')
      .insert(sampleUploads)
      .select();

    if (uploadsError) throw uploadsError;

    return {
      success: true,
      message: `Added ${uploads?.length || 0} sample file upload records!`,
      data: uploads
    };

  } catch (error) {
    console.error('Error adding sample file uploads:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Add sample submission data for testing exports
 */
export const addSampleSubmissions = async () => {
  try {
    // First, get the existing lead
    const { data: leads, error: leadsError } = await supabase
      .from('leads')
      .select('id')
      .limit(1);

    if (leadsError) throw leadsError;
    
    if (!leads || leads.length === 0) {
      throw new Error('No leads found. Please create a lead first.');
    }

    const leadId = leads[0].id;

    // Get product IDs
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, slug')
      .in('slug', ['property_all_risks', 'public_liability', 'workmens_comp']);

    if (productsError) throw productsError;

    if (!products || products.length === 0) {
      throw new Error('No products found.');
    }

    // Sample submissions data
    const sampleSubmissions = [
      {
        lead_id: leadId,
        product_id: products.find(p => p.slug === 'property_all_risks')?.id,
        answers_json: {
          propertyLocation: 'Dubai',
          ownOrLease: 'Own',
          sumInsured: 5000000,
          tradeLicense: 'trade_license_001.pdf',
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '+971501234567'
        }
      },
      {
        lead_id: leadId,
        product_id: products.find(p => p.slug === 'public_liability')?.id,
        answers_json: {
          businessActivity: 'Software Development and IT Services',
          customersVisit: 'Yes',
          numberOfEmployees: 25,
          annualTurnover: 8500000,
          operationLocations: 'Dubai Internet City, Abu Dhabi Global Market',
          tradeLicense: 'trade_license_001.pdf',
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '+971501234567'
        }
      },
      {
        lead_id: leadId,
        product_id: products.find(p => p.slug === 'workmens_comp')?.id,
        answers_json: {
          numberOfEmployees: 25,
          employeeRoles: 'Software Engineers, Project Managers, Sales Representatives, Administrative Staff',
          annualWages: 3600000,
          businessEmirate: 'Dubai',
          tradeLicense: 'trade_license_001.pdf',
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '+971501234567'
        }
      }
    ];

    // Insert sample submissions
    const { data: submissions, error: submissionsError } = await supabase
      .from('submissions')
      .insert(sampleSubmissions)
      .select();

    if (submissionsError) throw submissionsError;

    return {
      success: true,
      message: `Added ${submissions?.length || 0} sample submissions successfully!`,
      data: submissions
    };

  } catch (error) {
    console.error('Error adding sample submissions:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Clear all sample data (for testing purposes)
 */
export const clearSampleData = async () => {
  try {
    // Delete submissions first (due to foreign key constraints)
    const { error: submissionsError } = await supabase
      .from('submissions')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (submissionsError) throw submissionsError;

    // Delete lead contacts
    const { error: contactsError } = await supabase
      .from('lead_contacts')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (contactsError) throw contactsError;

    // Delete leads
    const { error: leadsError } = await supabase
      .from('leads')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (leadsError) throw leadsError;

    return {
      success: true,
      message: 'Sample data cleared successfully!'
    };

  } catch (error) {
    console.error('Error clearing sample data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get data counts for display
 */
export const getDataCounts = async () => {
  try {
    const [productsResult, questionsResult, leadsResult, submissionsResult] = await Promise.all([
      supabase.from('products').select('id', { count: 'exact', head: true }),
      supabase.from('questions').select('id', { count: 'exact', head: true }),
      supabase.from('leads').select('id', { count: 'exact', head: true }),
      supabase.from('submissions').select('id', { count: 'exact', head: true })
    ]);

    return {
      success: true,
      data: {
        products: productsResult.count || 0,
        questions: questionsResult.count || 0,
        leads: leadsResult.count || 0,
        submissions: submissionsResult.count || 0
      }
    };

  } catch (error) {
    console.error('Error getting data counts:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
